import { Component, OnInit, Output, EventEmitter, HostListener, Input, ChangeDetectorRef } from '@angular/core';
import { faCalendar, faBell, faCircle } from '@fortawesome/free-solid-svg-icons';
import { IndicatorsService, IndicatorRequestBody, IndicatorResponse, FilteredHeaderItem } from './service/filter-header.service';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { PermissionService } from 'src/app/util/services/permission/permission.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { StatusCiTypeService } from 'src/app/page/register-page/register-forms/cmdb/status-ci-type/service/status-ci-type.service';
import { StatusInterfaceCMDB } from 'src/app/util/interfaces/statusInterface';
import { all } from 'cypress/types/bluebird';
import { set } from 'cypress/types/lodash';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { MultiCheckboxSelectOption } from 'src/app/page_component_item/msp-multi-checkbox-select/msp-multi-checkbox-select.component';

/*
Componente header de filtros.
Permite a seleção de filtros como tipo de CI, localização, período de data e exibição de itens próprios.
Emite eventos quando os filtros são alterados.
*/

interface CiType {
	ciType: number;
	subType: number | string;
	type: string;
}

interface Location {
	id_location: string;
	site: string;
	location: string;
}

interface SelectedValues {
	show_mine: boolean;
	period_type: 'today' | 'week' | 'month' | 'custom';
	location: string;
	locations: string[];
	statuses: string[];
}
@Component({
	selector: 'pcp-filter-header',
	templateUrl: './filter-header.component.html',
	styleUrls: ['./filter-header.component.scss'],
})
export class FilterHeaderComponent implements OnInit {
	@Output() filterChange = new EventEmitter<any>();
	@Output() export = new EventEmitter<void>();

	icons = {
		faCalendar: faCalendar,
		faBell: faBell,
		faCircle: faCircle,
	};

	labels: LabelsInterface = {
		lbl_asset_tipo_ci: '',
		lbl_asset_localizacao: '',
		lbl_asset_meus_itens: '',
		lbl_geral_todos: '',
		lbl_localidade: '',
		lbl_geral_dia: '',
		lbl_geral_semana: '',
		lbl_geral_mes: '',
		lbl_geral_intervalo_superior_180_dias: '',
		lbl_status_ci: '',
		lbl_geral_selecionados: '',
		lbl_limpar_filtros: '',
		lbl_geral_pesquisar: '',
		lbl_geral_filtrar: '',
		lbl_vazio: 'Empty',
	};

	selectedValues: SelectedValues = {
		show_mine: true,
		period_type: 'month',
		location: '',
		locations: [],
		statuses: [],
	};

	selectedCiItem: CiType | '' = '';
	selectedCiItems: CiType[] = [];
	availableCiTypes: CiType[] = [];
	availableLocations: Location[] = [];
	availableStatus: StatusInterfaceCMDB[] = [];
	startDate = '';
	endDate = '';
	indicators: IndicatorResponse | null = null;

	// Arrays de backup — listas completas antes da filtragem pelos gráficos
	private allAvailableCiTypes: CiType[] = [];
	private allAvailableLocations: Location[] = [];
	private allAvailableStatus: StatusInterfaceCMDB[] = [];

	isDatePickerOpen = false;
	isLoadingLabels = true;
	isCiTypeDropdownOpen = false;
	isLocationDropdownOpen = false;
	isStatusDropdownOpen = false;
	isLoadingCiTypes = false;
	isLoadingLocations = false;
	isLoadingStatuses = false;

	constructor(
		private indicatorsService: IndicatorsService,
		private LabelsService: LabelsService,
		private NotificationService: NotificationService,
		public PermissionService: PermissionService,
		private ProfileService: ProfileService,
		private statusCiTypeService: StatusCiTypeService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		// Carrega os filtros do cache primeiro
		this.loadFiltersFromCache();

		this.LabelsService.getLabels().subscribe((labels) => {
			this.labels = labels;
			this.isLoadingLabels = false;
		});

		// Carrega as opções disponíveis
		this.loadAvailableCiTypes();
		this.loadAvailableLocations();
		this.loadAvailableStatus();
		this.getCustomerPermissions();

		// Aplica os filtros encadeados após carregar os dados do cache
		// Aguarda um pouco para garantir que os dados foram carregados
		setTimeout(() => {
			if (this.selectedCiItems.length > 0 || this.selectedValues.locations.length > 0 || this.selectedValues.statuses.length > 0) {
				this.applyChainedFilters();
			}
		}, 500);

		// Chamada feita para casos onde o filtro é carregado e a startDate e endDate não estão definidas, somente o periodType ao carregar o componente
		this.filterValues();
	}

	loadFiltersFromCache(): void {
		const cachedFilters = localStorage.getItem('cockpitFilters');
		if (cachedFilters) {
			try {
				const filters = JSON.parse(cachedFilters);

				// Aplicar os valores aos selectedValues
				if (filters.period_type) {
					this.selectedValues.period_type = filters.period_type;
				}
				if (filters.show_mine !== undefined) {
					this.selectedValues.show_mine = filters.show_mine;
				}
				if (filters.location !== undefined) {
					this.selectedValues.location = filters.location;
				}

				// Carregar arrays de seleções múltiplas
				if (filters.locations && Array.isArray(filters.locations)) {
					this.selectedValues.locations = filters.locations.map((loc) => loc.toString());
				}

				if (filters.statuses && Array.isArray(filters.statuses)) {
					this.selectedValues.statuses = filters.statuses.map((status) =>
						typeof status === 'object' ? status.id.toString() : status.toString()
					);
				}

				// Reconstruir selectedCiItems a partir de ciTypesArray
				if (filters.ciTypesArray && Array.isArray(filters.ciTypesArray) && filters.ciTypesArray.length > 0) {
					this.selectedCiItems = filters.ciTypesArray.map((item) => ({
						ciType: typeof item.ciType === 'string' ? Number.parseInt(item.ciType) : item.ciType,
						subType: item.subType === '' ? '' : typeof item.subType === 'string' ? Number.parseInt(item.subType) : item.subType,
						type: item.type || '',
					}));
				}

				// Reconstruir selectedCiItem se ciType existir (compatibilidade com versão antiga)
				this.selectedCiItem =
					filters.ciType !== undefined && filters.ciType !== ''
						? {
								ciType: typeof filters.ciType === 'string' ? Number.parseInt(filters.ciType) : filters.ciType,
								subType:
									filters.subType === ''
										? ''
										: typeof filters.subType === 'string'
											? Number.parseInt(filters.subType)
											: filters.subType,
								type: filters.ciTypeLabel || filters.type || '',
							}
						: '';

				this.startDate = filters.startDate || '';
				this.endDate = filters.endDate || '';
			} catch (error) {
				console.error('Erro ao carregar filtros do cache:', error);
			}
		}
	}

	saveFiltersToCache(): void {
		const filtersToCache = {
			period_type: this.selectedValues.period_type,
			show_mine: this.selectedValues.show_mine,
			location: this.selectedValues.location,
			locations: this.selectedValues.locations || [],
			statuses:
				this.selectedValues.statuses && this.selectedValues.statuses.length > 0
					? this.selectedValues.statuses.map((statusId) => {
							const statusObject = this.availableStatus.find((s) => s.id.toString() === statusId);
							return {
								id: statusId,
								status: statusObject ? statusObject.status : '',
							};
						})
					: [],
			ciType: this.selectedCiItem === '' ? '' : this.selectedCiItem.ciType,
			subType: this.selectedCiItem === '' ? '' : this.selectedCiItem.subType,
			type: this.selectedCiItem === '' ? '' : this.selectedCiItem.type,
			ciTypesArray: this.selectedCiItems.map((item) => ({
				ciType: item.ciType,
				subType: item.subType,
			})),
			startDate: this.startDate,
			endDate: this.endDate,
		};
		localStorage.setItem('cockpitFilters', JSON.stringify(filtersToCache));
	}

	calculateDates(): { startDate: string; endDate: string } {
		const today = new Date();
		const endDate = this.formatDate(today);
		let startDate: string;

		switch (this.selectedValues.period_type) {
			case 'today': {
				startDate = this.formatDate(today);
				break;
			}
			case 'week': {
				const weekAgo = new Date();
				weekAgo.setDate(today.getDate() - 7);
				startDate = this.formatDate(weekAgo);
				break;
			}
			case 'month': {
				const monthAgo = new Date();
				monthAgo.setMonth(today.getMonth() - 1);
				startDate = this.formatDate(monthAgo);
				break;
			}
			case 'custom': {
				startDate = this.startDate;
				const customEndDate = this.endDate || endDate;
				return {
					startDate,
					endDate: customEndDate,
				};
			}
			default: {
				startDate = endDate;
			}
		}

		return { startDate, endDate };
	}

	formatDate(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${day}/${month}/${year}`;
	}

	applyChainedFilters(): void {
		this.isLoadingCiTypes = true;
		this.isLoadingLocations = true;
		this.isLoadingStatuses = true;

		// Monta os parâmetros para a API de filtros encadeados
		const filterParameters: any = {
			id: '',
			type: '',
			identification: '',
			status: '',
			location: '',
			impact: '',
			sub_type: '',
			manufacturer: '',
			model: '',
			digital_term_status: '',
		};

		// Tipo de CI - envia como string separada por vírgulas se múltiplos
		if (this.selectedCiItems && this.selectedCiItems.length > 0) {
			const ciTypes = this.selectedCiItems.map((item) => item.ciType).join(',');
			filterParameters.type = ciTypes;

			// Preenche subtypes se estiverem definidos
			const subTypes = this.selectedCiItems
				.filter((item) => item.subType !== '' && item.subType !== null)
				.map((item) => item.subType)
				.join(',');
			if (subTypes) {
				filterParameters.sub_type = subTypes;
			}
		}

		// Status - envia como string separada por vírgulas se múltiplos
		if (this.selectedValues.statuses && this.selectedValues.statuses.length > 0) {
			const statuses = this.selectedValues.statuses
				.map((statusId) => {
					const statusObject = this.availableStatus.find((s) => s.id.toString() === statusId);
					return statusObject ? statusObject.status : '';
				})
				.filter((s) => s !== '')
				.join(',');
			filterParameters.status = statuses;
		}

		// Localizações - envia como string separada por vírgulas se múltiplas
		if (this.selectedValues.locations && this.selectedValues.locations.length > 0) {
			filterParameters.location = this.selectedValues.locations.join(',');
		}

		// Se não há nenhum filtro selecionado, restaura os valores originais
		const hasAnyFilter =
			this.selectedCiItems.length > 0 || this.selectedValues.locations.length > 0 || this.selectedValues.statuses.length > 0;

		this.availableCiTypes = [];
		this.availableLocations = [];
		this.availableStatus = [];

		if (hasAnyFilter) {
			// Se há filtros, aplica os filtros encadeados
			this.indicatorsService.getFilteredHeaders(filterParameters).subscribe({
				next: (response: FilteredHeaderItem[]) => {
					// Atualiza os tipos de CI disponíveis
					const typeField = response.find((field) => field.name === 'type');
					if (typeField && typeField.items) {
						const ciTypesFromAPI: CiType[] = [];
						for (const item of typeField.items) {
							ciTypesFromAPI.push({
								ciType: item.values[0] as number,
								subType: '',
								type: item.label,
							});
						}

						// Adiciona os subtipos disponíveis
						const subTypeField = response.find((field) => field.name === 'sub_type');
						if (subTypeField && subTypeField.items) {
							for (const item of subTypeField.items) {
								const subTypeValue = item.values[0] as number;
								// Procura o ciType correspondente nos dados completos
								const matchingCiType = this.allAvailableCiTypes.find((ci) => ci.subType === subTypeValue);

								if (matchingCiType) {
									ciTypesFromAPI.push({
										ciType: matchingCiType.ciType,
										subType: subTypeValue,
										type: item.label,
									});
								}
							}
						}
						this.availableCiTypes = ciTypesFromAPI;

						// Ordenar hierarquicamente: pais seguidos de seus filhos
						const parents = this.availableCiTypes.filter((item) => item.subType === '');
						const children = this.availableCiTypes.filter((item) => item.subType !== '');
						const ordered: CiType[] = [];

						for (const parent of parents) {
							ordered.push(parent);
							const parentChildren = children.filter((child) => child.ciType === parent.ciType);
							ordered.push(...parentChildren);
						}

						// Adiciona filhos órfãos (sem pai correspondente)
						const orphans = children.filter((child) => !parents.some((parent) => parent.ciType === child.ciType));
						ordered.push(...orphans);

						this.availableCiTypes = ordered;
					}

					// Atualiza as localizações disponíveis
					const locationField = response.find((field) => field.name === 'location');
					if (locationField && locationField.items) {
						this.availableLocations = locationField.items.map((item) => {
							const locationParts = item.label.split(' - ');
							return {
								id_location: item.values[0].toString(),
								site: locationParts[0] || '',
								location: locationParts[1] || item.label,
							};
						});
					}

					// Atualiza os status disponíveis
					const statusField = response.find((field) => field.name === 'status');
					if (statusField && statusField.items) {
						this.availableStatus = statusField.items
							.map((item) => {
								const matchingStatus = this.allAvailableStatus.find((s) => s.status === item.label);
								return {
									id: matchingStatus?.id?.toString() || '0',
									status: item.label,
									status_active: matchingStatus?.status_active || 1,
								};
							})
							.filter((s) => s.id !== '0');
					}

					this.isLoadingCiTypes = false;
					this.isLoadingLocations = false;
					this.isLoadingStatuses = false;
				},
				error: (error) => {
					console.error('Erro ao aplicar filtros encadeados:', error);
					this.isLoadingCiTypes = false;
					this.isLoadingLocations = false;
					this.isLoadingStatuses = false;
				},
			});
		} else {
			this.availableCiTypes = [...this.allAvailableCiTypes];
			this.availableLocations = [...this.allAvailableLocations];
			this.availableStatus = [...this.allAvailableStatus];

			this.isLoadingCiTypes = false;
			this.isLoadingLocations = false;
			this.isLoadingStatuses = false;
		}
	}

	filterValues(): void {
		// Atualiza startDate e endDate com os valores calculados
		const dates = this.calculateDates();
		this.startDate = dates.startDate;
		this.endDate = dates.endDate;

		// Salva os filtros no cache
		this.saveFiltersToCache();

		// Monta os parâmetros para enviar para a API
		const parameters: any = {
			startDate: dates.startDate,
			endDate: dates.endDate,
		};

		// Adiciona location se estiver preenchido (mantém compatibilidade com seleção única)
		if (this.selectedValues.location) {
			parameters.location = this.selectedValues.location;
		}

		// Adiciona locations se múltiplas localizações estiverem selecionadas
		if (this.selectedValues.locations && this.selectedValues.locations.length > 0) {
			parameters.locations = this.selectedValues.locations;
		}

		// Adiciona statuses se múltiplos status estiverem selecionados
		if (this.selectedValues.statuses && this.selectedValues.statuses.length > 0) {
			parameters.statuses = this.selectedValues.statuses.map((statusId) => {
				const statusObject = this.availableStatus.find((s) => s.id.toString() === statusId);
				return {
					id: statusId,
					status: statusObject ? statusObject.status : '',
				};
			});
		}

		// Adiciona ciType e subtype se um item CI estiver selecionado (mantém compatibilidade)
		if (this.selectedCiItem !== '') {
			parameters.ciType = this.selectedCiItem.ciType;
			if (this.selectedCiItem.subType) {
				parameters.subtype = this.selectedCiItem.subType;
			}
		}

		// Adiciona ciTypes se múltiplos tipos estiverem selecionados
		if (this.selectedCiItems && this.selectedCiItems.length > 0) {
			parameters.ciTypes = this.selectedCiItems.map((item) => ({
				ciType: item.ciType,
				subType: item.subType,
			}));
		}

		this.filterChange.emit({
			period_type: this.selectedValues.period_type,
			show_mine: this.selectedValues.show_mine,
			location: this.selectedValues.location,
			locations: this.selectedValues.locations,
			statuses:
				this.selectedValues.statuses && this.selectedValues.statuses.length > 0
					? this.selectedValues.statuses.map((statusId) => {
							const statusObject = this.availableStatus.find((s) => s.id.toString() === statusId);
							return {
								id: statusId,
								status: statusObject ? statusObject.status : '',
							};
						})
					: [],
			startDate: dates.startDate,
			endDate: dates.endDate,
			ciType: this.selectedCiItem === '' ? '' : this.selectedCiItem.ciType,
			subType: this.selectedCiItem === '' ? '' : this.selectedCiItem.subType,
			type: this.selectedCiItem === '' ? '' : this.selectedCiItem.type,
			ciTypes: this.selectedCiItems,
			ciTypesArray: this.selectedCiItems.map((item) => ({
				ciType: item.ciType,
				subType: item.subType,
			})),
		});

		this.applyChainedFilters();
	}

	openDatePicker(): void {
		this.isDatePickerOpen = true;
		this.selectedValues.period_type = 'custom';
	}

	private convertDateFormat(dateString: string): string {
		const [day, month, year] = dateString.split('/');
		return `${day}/${month}/${year}`;
	}

	selectDate(dateRange: string[]): void {
		const selectedDate = dateRange
			.map((date) => {
				const newDate = new Date(date);
				return `${String(newDate.getDate()).padStart(2, '0')}/${String(newDate.getMonth() + 1).padStart(2, '0')}/${newDate.getFullYear()}`;
			})
			.join(' - ');
		const [startDate, endDate] = selectedDate.split(' - ');
		this.startDate = this.convertDateFormat(startDate.trim());
		this.endDate = this.convertDateFormat(endDate.trim());

		const startDateObject = new Date(this.startDate.split('/').reverse().join('/'));
		const endDateObject = new Date(this.endDate.split('/').reverse().join('/'));

		const timeDiff = endDateObject.getTime() - startDateObject.getTime();
		const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

		if (diffDays > 180) {
			this.NotificationService.setNotificationMessage(this.labels.lbl_geral_intervalo_superior_180_dias);
			this.endDate = '';
			return;
		}

		this.isDatePickerOpen = false;
	}

	toggleCiTypeDropdown(event?: Event): void {
		if (event) {
			event.stopPropagation();
		}
		this.isCiTypeDropdownOpen = !this.isCiTypeDropdownOpen;
		if (this.isCiTypeDropdownOpen) {
			this.isLocationDropdownOpen = false;
			this.isStatusDropdownOpen = false;
		}
	}

	toggleLocationDropdown(event?: Event): void {
		if (event) {
			event.stopPropagation();
		}
		this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
		if (this.isLocationDropdownOpen) {
			this.isCiTypeDropdownOpen = false;
			this.isStatusDropdownOpen = false;
		}
	}

	toggleStatusDropdown(event?: Event): void {
		if (event) {
			event.stopPropagation();
		}
		this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
		if (this.isStatusDropdownOpen) {
			this.isCiTypeDropdownOpen = false;
			this.isLocationDropdownOpen = false;
		}
	}

	toggleCiTypeSelection(item: CiType): void {
		const index = this.selectedCiItems.findIndex((selected) => selected.ciType === item.ciType && selected.subType === item.subType);

		if (index > -1) {
			this.selectedCiItems.splice(index, 1);
		} else {
			this.selectedCiItems.push(item);
		}
	}

	getCiTypeOptions(): MultiCheckboxSelectOption[] {
		return this.availableCiTypes.map((item) => ({
			value: this.getCiTypeSelectionValue(item),
			label: this.labels[item.type] || item.type,
			group_name: item.subType && item.subType != '' ? 'subtype' : 'type',
			emphasized: item.subType === '',
			selected: false,
		}));
	}

	getSelectedCiTypeValues(): string[] {
		return this.selectedCiItems.map((item) => this.getCiTypeSelectionValue(item));
	}

	handleCiTypeSelectionChange(selectedValues: string[]): void {
		this.selectedCiItems = this.availableCiTypes.filter((item) => selectedValues.includes(this.getCiTypeSelectionValue(item)));
	}

	private getCiTypeSelectionValue(item: CiType): string {
		return `${item.ciType}::${item.subType ?? ''}`;
	}

	toggleLocationSelection(locationId: string): void {
		const locationIdString = locationId.toString();
		const index = this.selectedValues.locations.indexOf(locationIdString);

		if (index > -1) {
			this.selectedValues.locations.splice(index, 1);
		} else {
			this.selectedValues.locations.push(locationIdString);
		}
	}

	toggleStatusSelection(statusId: string): void {
		const index = this.selectedValues.statuses.indexOf(statusId);

		if (index > -1) {
			this.selectedValues.statuses.splice(index, 1);
		} else {
			this.selectedValues.statuses.push(statusId);
		}
	}

	isCiTypeSelected(item: CiType): boolean {
		return this.selectedCiItems.some((selected) => selected.ciType === item.ciType && selected.subType === item.subType);
	}

	isLocationSelected(locationId: string): boolean {
		return this.selectedValues.locations.includes(locationId.toString());
	}

	isStatusSelected(statusId: string): boolean {
		return this.selectedValues.statuses.includes(statusId);
	}

	getCiTypeSelectionLabel(): string {
		if (this.selectedCiItems.length === 0) {
			return this.labels.lbl_geral_todos;
		} else if (this.selectedCiItems.length === 1) {
			return this.labels[this.selectedCiItems[0].type] || this.selectedCiItems[0].type;
		} else {
			return `${this.selectedCiItems.length} ${this.labels.lbl_geral_selecionados}`;
		}
	}

	getLocationSelectionLabel(): string {
		if (this.selectedValues.locations.length === 0) {
			return this.labels.lbl_geral_todos;
		} else if (this.selectedValues.locations.length === 1) {
			const location = this.availableLocations.find(
				(loc) => loc.id_location.toString() === this.selectedValues.locations[0].toString()
			);
			return location ? `${location.site} - ${location.location}` : '';
		} else {
			return `${this.selectedValues.locations.length} ${this.labels.lbl_geral_selecionados}`;
		}
	}

	getStatusSelectionLabel(): string {
		if (this.selectedValues.statuses.length === 0) {
			return this.labels.lbl_geral_todos;
		} else if (this.selectedValues.statuses.length === 1) {
			const status = this.availableStatus.find((s) => s.id.toString() === this.selectedValues.statuses[0]);
			return status ? status.status : '';
		} else {
			return `${this.selectedValues.statuses.length} ${this.labels.lbl_geral_selecionados}`;
		}
	}

	isAllCiTypesSelected(): boolean {
		return this.selectedCiItems.length === 0;
	}

	isAllLocationsSelected(): boolean {
		return this.selectedValues.locations.length === 0;
	}

	isAllStatusesSelected(): boolean {
		return this.selectedValues.statuses.length === 0;
	}

	selectAllCiTypes(): void {
		this.selectedCiItems = [];
	}

	selectAllLocations(): void {
		this.selectedValues.locations = [];
	}

	selectAllStatuses(): void {
		this.selectedValues.statuses = [];
	}

	loadAvailableCiTypes(): void {
		this.isLoadingCiTypes = true;
		this.indicatorsService.getAvailablesCiTypes().subscribe({
			next: (response) => {
				this.availableCiTypes = response.map((item: any) => ({
					ciType: item.ciType,
					subType: item.subType || '',
					type: item.type || '',
				}));
				this.allAvailableCiTypes = [...this.availableCiTypes];

				// Sincroniza selectedCiItem com o objeto da lista se vier do cache
				if (this.selectedCiItem !== '') {
					const cachedItem = this.selectedCiItem;
					const cachedCiType = typeof cachedItem.ciType === 'string' ? Number.parseInt(cachedItem.ciType) : cachedItem.ciType;
					const cachedSubType =
						cachedItem.subType === ''
							? ''
							: typeof cachedItem.subType === 'string'
								? Number.parseInt(cachedItem.subType)
								: cachedItem.subType;

					const matchingItem = this.availableCiTypes.find(
						(item) => item.ciType === cachedCiType && item.subType === cachedSubType
					);

					if (matchingItem) {
						this.selectedCiItem = matchingItem;
					}
				}

				// Sincroniza selectedCiItems (array) com os objetos da lista se vieram do cache
				if (this.selectedCiItems.length > 0) {
					const syncedItems: CiType[] = [];
					for (const cachedItem of this.selectedCiItems) {
						const cachedCiType = typeof cachedItem.ciType === 'string' ? Number.parseInt(cachedItem.ciType) : cachedItem.ciType;
						const cachedSubType =
							cachedItem.subType === ''
								? ''
								: typeof cachedItem.subType === 'string'
									? Number.parseInt(cachedItem.subType)
									: cachedItem.subType;

						const matchingItem = this.availableCiTypes.find(
							(item) => item.ciType === cachedCiType && item.subType === cachedSubType
						);

						if (matchingItem) {
							syncedItems.push(matchingItem);
						}
					}
					this.selectedCiItems = syncedItems;
				}
				this.isLoadingCiTypes = false;
			},
			error: (error) => {
				console.error('Erro ao carregar tipos de CI:', error);
				this.isLoadingCiTypes = false;
			},
		});
	}

	loadAvailableLocations(): void {
		this.isLoadingLocations = true;
		this.indicatorsService.getAvailablesLocations().subscribe({
			next: (response) => {
				this.allAvailableLocations = response;
				this.availableLocations = response;

				// Sincroniza selectedValues.locations com os IDs disponíveis
				if (this.selectedValues.locations.length > 0) {
					const validLocations = this.selectedValues.locations.filter((locationId) =>
						this.availableLocations.some((loc) => loc.id_location.toString() === locationId.toString())
					);
					this.selectedValues.locations = validLocations;
				}
				this.isLoadingLocations = false;
			},
			error: (error) => {
				console.error('Erro ao carregar localizações:', error);
				this.isLoadingLocations = false;
			},
		});
	}

	loadAvailableStatus(): void {
		this.isLoadingStatuses = true;
		this.statusCiTypeService.getStatusCiType().subscribe({
			next: (response) => {
				this.allAvailableStatus = response;
				this.availableStatus = response;

				// Sincroniza selectedValues.statuses com os IDs disponíveis
				if (this.selectedValues.statuses.length > 0) {
					const validStatuses = this.selectedValues.statuses.filter((statusId) =>
						this.availableStatus.some((s) => s.id.toString() === statusId)
					);
					//this.selectedValues.statuses = validStatuses;
				}
				this.isLoadingStatuses = false;
			},
			error: (error) => {
				console.error('Erro ao carregar status:', error);
				this.isLoadingStatuses = false;
			},
		});
	}

	getCustomerPermissions(): any {
		this.PermissionService.getCustomersPermissions();
	}

	clearFilters(): void {
		this.isLoadingCiTypes = true;
		this.isLoadingLocations = true;
		this.isLoadingStatuses = true;

		// Reseta todos os valores para os padrões
		this.selectedValues = {
			show_mine: true,
			period_type: 'month',
			location: '',
			locations: [],
			statuses: [],
		};
		this.selectedCiItem = '';
		this.selectedCiItems = [];
		this.startDate = '';
		this.endDate = '';

		this.availableCiTypes = [];
		this.availableLocations = [];
		this.availableStatus = [];

		// Remove do cache
		localStorage.removeItem('cockpitFilters');

		// Emite o evento com valores limpos
		this.filterValues();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event): void {
		this.isCiTypeDropdownOpen = false;
		this.isLocationDropdownOpen = false;
		this.isStatusDropdownOpen = false;
	}
}
